import AddIcon from "@mui/icons-material/Add";
import DragHandleIcon from "@mui/icons-material/DragHandle";
import { Box, Button, Dialog } from "@mui/material";
import { Theme, useTheme } from "@mui/material/styles";
import ConfirmDialog from "components/ConfirmDialog";
import DeleteIconButton from "components/IconButtons/DeleteIconButton";
import EditIconButton from "components/IconButtons/EditIconButton";
import LazyTable, { TableCellType } from "components/LazyTable";
import SnackbarMessage from "components/SnackbarMessage";
import { withErrorBoundary } from "hocs/withErrorBoundary";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import { useStrictQuery } from "hooks/useStrictQuery";
import { useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQueryClient } from "react-query";
import {
  AccessLevelListRequest,
  IAccessLevelModel,
  ListResponseOfAccessLevelModel,
  OrganizationDetails,
  UpdateAccessLevelsSequenceRequest,
} from "services/KnowfullyAdminClient";
import { SnackbarMessageProps } from "types/common-types";
import AccessLevelForm from "./AccessLevelForm";

type OrganizationAccessLevelsProps = {
  organization: OrganizationDetails;
};

const useStyles = (theme: Theme) => ({
  addAccessLevelButton: {
    display: "flex",
    justifyContent: "end",
    width: "100%",
    margin: theme.spacing(4, 0, 3, 0),
  },
  root: {
    width: "100%",
  },
  buttons: {
    display: "flex",
  },
});

const OrganizationAccessLevels = (props: OrganizationAccessLevelsProps) => {
  const { organization } = props;
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { accessLevelService } = useGlobalContext();
  const [updateOpen, updateOpenActions] = useBoolean(false);
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const [
    accessLevelToUpdate,
    accessLevelToUpdateActions,
  ] = useState<IAccessLevelModel | null>(null);
  const [accessLevelIdToDelete, accessLevelIdToDeleteActions] = useState<
    string | null
  >(null);
  const queryClient = useQueryClient();
  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const accessLevelsData = useStrictQuery<ListResponseOfAccessLevelModel>(
    ["accessLevels", organization.id],
    () =>
      accessLevelService.getAccessLevels(
        AccessLevelListRequest.fromJS({
          pageSize: -1,
          sortField: "sequence",
          organizationId: organization.id,
        })
      ),
    {
      keepPreviousData: true,
      enabled: !!organization.id,
    }
  );

  let accessLevels = accessLevelsData.data?.data ?? [];

  const updateAccessLevelsSequence = async (
    orderedAccessLevels: IAccessLevelModel[]
  ) => {
    const orderedAccessLevelIds = orderedAccessLevels.map(
      (accessLevel) => accessLevel.id
    );
    await accessLevelService.updateAccessLevelsSequence(
      new UpdateAccessLevelsSequenceRequest({
        accessLevelIds: orderedAccessLevelIds,
        organizationId: organization.id,
      })
    );

    queryClient.invalidateQueries(["accessLevels", organization.id]);
  };

  const onSave = () => {
    updateOpenActions.setFalse();
    accessLevelToUpdateActions(null);

    queryClient.invalidateQueries(["accessLevels", organization.id]);
  };

  const onCancel = () => {
    updateOpenActions.setFalse();
    accessLevelToUpdateActions(null);
  };

  const onDelete = async () => {
    await accessLevelService.deleteAccessLevel(accessLevelIdToDelete!);
    const orderedAccessLevels = [...accessLevels];
    const index = orderedAccessLevels.findIndex(
      (d) => d.id === accessLevelIdToDelete
    );
    if (index > -1) {
      orderedAccessLevels.splice(index, 1);
    }
    await updateAccessLevelsSequence(orderedAccessLevels);
    deleteOpenActions.setFalse();
  };

  const Buttons = ({ value }: { value: IAccessLevelModel }) => {
    return (
      <Box sx={styles.buttons}>
        <EditIconButton
          onClick={(e) => {
            e.stopPropagation();
            accessLevelToUpdateActions(value);
            updateOpenActions.setTrue();
          }}
          data-cy={`${value.sequence}EditButton`}
        />
        <DeleteIconButton
          onClick={(e) => {
            e.stopPropagation();
            deleteOpenActions.setTrue();
            accessLevelIdToDeleteActions(value.id);
          }}
          data-cy={`${value.sequence}DeleteButton`}
        />
      </Box>
    );
  };

  const onDragEndHandler = async (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const orderedAccessLevels = [...accessLevels];
    const dragAccessLevel = orderedAccessLevels[result.source.index];
    orderedAccessLevels.splice(result.source.index, 1);
    orderedAccessLevels.splice(result.destination.index, 0, dragAccessLevel);

    await updateAccessLevelsSequence(orderedAccessLevels);
  };

  const cells: Array<TableCellType> = [
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: DragHandleIcon,
      label: "",
      noSort: true,
    } as TableCellType,
    {
      accessor: "sku",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "skuLabel" }),
    },
    {
      accessor: "skuTitle",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "skuTitleColumnLabel" }),
    },
    {
      accessor: "displayName",
      align: "left",
      width: 0,
      type: "text",
      label: t({ id: "displayNameLabel" }),
    },
    {
      accessor: "value",
      align: "center",
      width: 1,
      type: "node",
      View: Buttons,
      label: "",
      noSort: true,
    },
  ];

  return (
    <Box sx={styles.root}>
      <LazyTable
        {...{
          rowCount: accessLevelsData.data?.totalRecords ?? 0,
          rows: accessLevels,
          loading: accessLevelsData.isFetching,
          titleComponent: (
            <Box sx={styles.addAccessLevelButton}>
              <Button
                onClick={updateOpenActions.setTrue}
                startIcon={<AddIcon />}
                data-cy="addAccessLevel"
              >
                <T id="addAccessLevelButton" />
              </Button>
            </Box>
          ),
          dense: true,
          cells,
          page: 0,
          order: "asc",
          orderBy: "sequence",
          rowsPerPage: accessLevels.length,
          setPage: () => {},
          setOrder: () => {},
          setOrderBy: () => {},
          setRowsPerPage: () => {},
          allowPagination: false,
          allowDragAndDrop: true,
          onDragAndDropEnd: onDragEndHandler,
        }}
      />

      <Dialog open={updateOpen} maxWidth="sm" fullWidth disableEnforceFocus>
        <AccessLevelForm
          onAccessLevelSaved={onSave}
          onCancel={onCancel}
          organization={organization}
          maxSequence={accessLevels?.length}
          accessLevel={accessLevelToUpdate ?? undefined}
          submitLabel={
            accessLevelToUpdate != null
              ? t({ id: "updateAccessLevelButton" })
              : t({ id: "addAccessLevelButton" })
          }
        />
      </Dialog>
      <ConfirmDialog
        dialogTitleKey="deleteAccessLevelTitle"
        contentKey="deleteAccessLevelQuestion"
        open={deleteOpen}
        onCancel={deleteOpenActions.setFalse}
        handleConfirm={onDelete}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </Box>
  );
};

export default withErrorBoundary(OrganizationAccessLevels);
